import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';
import { Alert, Box, Button, Chip, InputAdornment, Link, TextField, Tooltip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ReceiptIcon from '@mui/icons-material/ReceiptOutlined';
import { listFinanceReceipts } from '../../api/finance';
import { downloadReceipt, getReceiptBlob } from '../../api/payments';
import { useBranchFilter } from '../../context/branchContext';
import {
  DocumentPreviewDialog,
  DocumentPreviewTarget,
} from '../../components/DocumentPreviewDialog';
import { PageHeader } from '../../components/PageHeader';
import { TableToolbar } from '../../components/table/TableToolbar';
import { FinanceFilters, FinanceReceiptRow } from '../../types/finance';
import { ExportButtons } from './ExportButtons';
import { FinanceFilterBar } from './FinanceFilterBar';
import { formatDate, formatMoney } from './financeFormat';

/** Finance → Receipts (Phase 96): receipts issued when a payment was
 * confirmed, with the payment's stored amount, SSCL and LKR. */
export const FinanceReceiptsPage = () => {
  const { selectedBranchId } = useBranchFilter();
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 50,
  });
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<FinanceFilters>({});
  const [preview, setPreview] = useState<DocumentPreviewTarget | null>(null);
  const resetPage = () => setPaginationModel((p) => ({ ...p, page: 0 }));

  const params = { ...filters, branchId: selectedBranchId, search: search || undefined };
  const listQuery = useQuery({
    queryKey: ['finance-receipts', paginationModel, params],
    queryFn: () =>
      listFinanceReceipts({
        ...params,
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
      }),
    placeholderData: (prev) => prev,
  });

  const columns: GridColDef<FinanceReceiptRow>[] = [
    {
      field: 'receiptNumber',
      headerName: 'Receipt',
      width: 150,
      renderCell: ({ row }) =>
        row.status === 'VOID' ? (
          <Tooltip title={`Voided ${formatDate(row.voidedAt)}${row.voidedBy ? ` by ${row.voidedBy}` : ''} — the approval was reversed${row.voidReason ? `: ${row.voidReason}` : ''}`}>
            <span>
              <s>{row.receiptNumber}</s> <Chip size="small" color="error" variant="outlined" label="VOID" sx={{ height: 18, fontSize: 10 }} />
            </span>
          </Tooltip>
        ) : (
          row.receiptNumber
        ),
    },
    { field: 'issuedAt', headerName: 'Issued', width: 110, valueFormatter: (v) => formatDate(v) },
    { field: 'paymentNumber', headerName: 'Payment', width: 110 },
    {
      field: 'student',
      headerName: 'Student',
      flex: 1,
      minWidth: 170,
      sortable: false,
      renderCell: ({ row }) => (
        <Link
          component={RouterLink}
          to={`/finance/students/${row.student.id}?paymentId=${row.paymentId}`}
        >
          {row.student.name} ({row.student.studentNumber})
        </Link>
      ),
    },
    { field: 'branch', headerName: 'Branch', width: 110, valueFormatter: (v) => v ?? '—' },
    {
      field: 'category',
      headerName: 'Category',
      width: 180,
      valueGetter: (_v, row) => row.category.name,
    },
    {
      field: 'totalWithTax',
      headerName: 'Total incl. SSCL',
      width: 140,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (_v, row) => formatMoney(row.totalWithTax, row.currency),
    },
    {
      field: 'lkrAmount',
      headerName: 'LKR',
      width: 120,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (v: number | null) =>
        v == null ? '—' : v.toLocaleString('en-US', { minimumFractionDigits: 2 }),
    },
    { field: 'issuedBy', headerName: 'Issued by', width: 140 },
    {
      field: 'actions',
      headerName: '',
      width: 150,
      sortable: false,
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Button
            size="small"
            startIcon={<ReceiptIcon />}
            onClick={() =>
              setPreview({
                key: `rct-${row.id}`,
                originalName: `${row.receiptNumber}.pdf`,
                mimeType: 'application/pdf',
                fetchBlob: () => getReceiptBlob(row.paymentId, row.status === 'VOID' ? row.id : undefined),
                onDownload: () => void downloadReceipt(row.paymentId, row.receiptNumber, row.status === 'VOID' ? row.id : undefined),
              })
            }
          >
            View
          </Button>
          <Button
            size="small"
            onClick={() => void downloadReceipt(row.paymentId, row.receiptNumber, row.status === 'VOID' ? row.id : undefined)}
          >
            PDF
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        icon={ReceiptIcon}
        color="#6A1B9A"
        title="Receipts"
        subtitle="Receipts issued for confirmed payments."
        actions={<ExportButtons params={{ report: 'receipts', ...params }} />}
      />
      <FinanceFilterBar
        value={filters}
        onChange={(next) => {
          setFilters(next);
          resetPage();
        }}
      >
        <TextField
          placeholder="Search receipt, payment or student…"
          size="small"
          sx={{ width: 260 }}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            resetPage();
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </FinanceFilterBar>
      {listQuery.isError ? (
        <Alert severity="error">Couldn't load receipts.</Alert>
      ) : (
        <Box sx={{ height: 640, bgcolor: 'background.paper', borderRadius: 1 }}>
          <DataGrid
            rows={listQuery.data?.data ?? []}
            columns={columns}
            rowCount={listQuery.data?.meta.total ?? 0}
            loading={listQuery.isFetching}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[20, 50, 100]}
            slots={{ toolbar: TableToolbar }}
            localeText={{ noRowsLabel: 'No receipts match these filters.' }}
            disableRowSelectionOnClick
          />
        </Box>
      )}
      <DocumentPreviewDialog target={preview} onClose={() => setPreview(null)} />
    </Box>
  );
};
